import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { adminAPI } from '../lib/api';
import {
  Shield,
  Users,
  Activity,
  Trophy,
  MapPin,
  Building,
  Database,
  Loader2,
  UserPlus,
  Target,
} from 'lucide-react';

const roleOptions = [
  { value: 'platform_admin', label: 'Platform Admin', color: 'bg-red-500' },
  { value: 'club_admin', label: 'Club Admin', color: 'bg-blue-500' },
  { value: 'venue_admin', label: 'Venue Admin', color: 'bg-purple-500' },
  { value: 'tournament_admin', label: 'Tournament Admin', color: 'bg-amber-500' },
  { value: 'player', label: 'Player', color: 'bg-green-500' },
  { value: 'umpire', label: 'Umpire', color: 'bg-cyan-500' },
];

const skillLevels = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'professional', label: 'Professional' },
];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newPlayer, setNewPlayer] = useState({
    name: '',
    email: '',
    role: 'player',
    skill_level: 'beginner',
  });

  useEffect(() => {
    if (user?.role !== 'platform_admin' && user?.role !== 'admin') {
      toast.error('Access denied. Platform Admin role required.');
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [statsRes, usersRes] = await Promise.all([
          adminAPI.getStats(),
          adminAPI.getAllUsers(),
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data || []);
      } catch (error) {
        console.error('Error fetching admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const handleSeedDemoData = async () => {
    setSeeding(true);
    try {
      await adminAPI.seedDemoData();
      toast.success('Demo data seeded successfully!');
      
      // Refresh stats and users
      const [statsRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAllUsers(),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleCreatePlayer = async () => {
    if (!newPlayer.name || !newPlayer.email) {
      toast.error('Name and email are required');
      return;
    }

    setCreating(true);
    try {
      const response = await adminAPI.createPlayer(newPlayer);
      toast.success(`User created! Default password: ${response.data.default_password}`);
      setCreateDialogOpen(false);
      setNewPlayer({ name: '', email: '', role: 'player', skill_level: 'beginner' });
      
      // Refresh users
      const usersRes = await adminAPI.getAllUsers();
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create player');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      setUsers(users.map((u) => (u.user_id === userId ? { ...u, role: newRole } : u)));
      toast.success('User role updated');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const getRoleBadgeColor = (role) => {
    const found = roleOptions.find((r) => r.value === role);
    return found?.color || 'bg-gray-500';
  };

  const getRoleLabel = (role) => {
    const found = roleOptions.find((r) => r.value === role);
    return found?.label || role;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-red-500" />
            <div>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Admin Dashboard</h1>
              <p className="text-muted-foreground mt-1">Manage users, players, and system data</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-secondary hover:bg-secondary/90" data-testid="add-user-btn">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                  <DialogDescription>
                    Add a new user with any role. Default password will be provided.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={newPlayer.name}
                      onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })}
                      data-testid="player-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={newPlayer.email}
                      onChange={(e) => setNewPlayer({ ...newPlayer, email: e.target.value })}
                      data-testid="player-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select
                      value={newPlayer.role}
                      onValueChange={(value) => setNewPlayer({ ...newPlayer, role: value })}
                    >
                      <SelectTrigger data-testid="player-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roleOptions.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Skill Level</Label>
                    <Select
                      value={newPlayer.skill_level}
                      onValueChange={(value) => setNewPlayer({ ...newPlayer, skill_level: value })}
                    >
                      <SelectTrigger data-testid="player-skill-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {skillLevels.map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreatePlayer}
                    disabled={creating}
                    className="bg-secondary hover:bg-secondary/90"
                    data-testid="submit-user-btn"
                  >
                    {creating ? 'Creating...' : 'Create User'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Button
              onClick={handleSeedDemoData}
              disabled={seeding}
              className="bg-accent hover:bg-accent/90"
              data-testid="seed-demo-btn"
            >
              {seeding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  Seed Demo Data
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
              <p className="text-xs text-muted-foreground">Users</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Activity className="h-6 w-6 mx-auto mb-2 text-secondary" />
              <p className="text-2xl font-bold">{stats?.total_matches || 0}</p>
              <p className="text-xs text-muted-foreground">Matches</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="h-6 w-6 mx-auto mb-2 rounded-full bg-red-500 flex items-center justify-center">
                <span className="text-white text-xs font-bold">{stats?.live_matches || 0}</span>
              </div>
              <p className="text-2xl font-bold">{stats?.live_matches || 0}</p>
              <p className="text-xs text-muted-foreground">Live</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Building className="h-6 w-6 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{stats?.total_clubs || 0}</p>
              <p className="text-xs text-muted-foreground">Clubs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Trophy className="h-6 w-6 mx-auto mb-2 text-accent" />
              <p className="text-2xl font-bold">{stats?.total_tournaments || 0}</p>
              <p className="text-xs text-muted-foreground">Tournaments</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <MapPin className="h-6 w-6 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{stats?.total_venues || 0}</p>
              <p className="text-xs text-muted-foreground">Venues</p>
            </CardContent>
          </Card>
        </div>

        {/* Users Management */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Management
            </CardTitle>
            <CardDescription>
              Manage user roles and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No users found. Seed demo data or add players manually.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u.user_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-muted/50 gap-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary">
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Badge className={`${getRoleBadgeColor(u.role)} capitalize`}>
                        {getRoleLabel(u.role)}
                      </Badge>
                      
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleRoleChange(u.user_id, value)}
                      >
                        <SelectTrigger className="w-[160px]" data-testid={`role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roleOptions.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
